import { Input } from "@repo/ui/components/ui/input"
import { Body, Label } from "@repo/ui/components/ui/typography"
import { useTranslateFieldError } from "@repo/ui/lib/i18n"
import { cn } from "@repo/ui/lib/utils"
import { useFieldContext } from "@repo/ui/tanstack-form"
import * as React from "react"

interface TextFieldProps
  extends Omit<
    React.ComponentProps<typeof Input>,
    "name" | "value" | "onChange"
  > {
  label?: string
  description?: string
}

export function TextField({
  label,
  description,
  className,
  ...props
}: TextFieldProps) {
  const field = useFieldContext<string>()
  const translateFieldError = useTranslateFieldError()

  return (
    <div className="space-y-2">
      {label && (
        <Label
          size="m"
          weight="medium"
          htmlFor={field.name}
          className="leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
        >
          {label}
        </Label>
      )}
      <Input
        id={field.name}
        name={field.name}
        value={field.state.value ?? ""}
        onChange={(e) => field.handleChange(e.target.value)}
        onBlur={field.handleBlur}
        className={cn(
          field.state.meta.errors.length > 0 && "border-destructive",
          className,
        )}
        {...props}
      />
      {description && (
        <Body size="m" className="text-muted-foreground">
          {description}
        </Body>
      )}
      {field.state.meta.errors.length > 0 && (
        <Body size="m" weight="medium" className="text-destructive">
          {field.state.meta.errors
            .map((error) => translateFieldError(String(error)))
            .join(", ")}
        </Body>
      )}
    </div>
  )
}
